import React, { useEffect, useState } from 'react'
import CLOSE_DEFAULT from '../../../../public/assets/window-close.svg'
import WINDOW_ICON from '../../../../public/assets/window-white.svg'
import BaseButton from '../../../components/common/BaseButton'
import type { NarrativeOcgSeverity, NarrativeOcgValidationHint } from '../types'
import {
  getIssueCount,
  getIssueDetails,
  getIssueTitle,
} from '../utils/issueDetails'
import { NarrativeOcgMobileFullScreen } from './NarrativeOcgMobileFullScreen'
import { cn } from '@/utils/mergeClassNames'

const stopFloatingEvent = (event: React.SyntheticEvent) => {
  event.preventDefault()
  event.stopPropagation()
  event.nativeEvent.stopImmediatePropagation?.()
}

const handleFloatingWheel = (
  event: React.WheelEvent,
  onInteraction?: () => void
) => {
  onInteraction?.()
  event.stopPropagation()
  event.nativeEvent?.stopImmediatePropagation?.()

  if (event.target === event.currentTarget) {
    event.preventDefault()
  }
}

export const NarrativeOcgIssueDetailsDialog = ({
  validationHint,
  severity,
  onClose,
  onFloatingWindowInteraction,
  isMobile = false,
}: {
  validationHint?: NarrativeOcgValidationHint
  severity: NarrativeOcgSeverity
  onClose: () => void
  onFloatingWindowInteraction?: (isOpen?: boolean) => void
  isMobile?: boolean
}) => {
  const [maximized, setMaximized] = useState(false)
  const count = getIssueCount(validationHint, severity)
  const details = getIssueDetails(validationHint, severity)

  useEffect(() => {
    onFloatingWindowInteraction?.(true)
    const interactionInterval = window.setInterval(() => {
      onFloatingWindowInteraction?.()
    }, 500)

    return () => {
      window.clearInterval(interactionInterval)
      onFloatingWindowInteraction?.(false)
    }
  }, [onFloatingWindowInteraction])

  const handleClose = (event: React.SyntheticEvent) => {
    onFloatingWindowInteraction?.()
    stopFloatingEvent(event)
    onClose()
  }
  const content = (
    <>
      {details.map((detail, index) => (
        <div
          className="narrative-ocg-detail-item"
          key={`${detail.title}-${index}`}
        >
          <div className="mb-[6px] font-bold">• {detail.title}</div>
          {detail.description ? (
            <div className="narrative-ocg-detail-item__description">
              {detail.description}
            </div>
          ) : null}
        </div>
      ))}
    </>
  )

  if (isMobile) {
    return (
      <NarrativeOcgMobileFullScreen
        title={getIssueTitle(severity, count)}
        variant={severity === 'error' ? 'error' : 'warning'}
        onClose={onClose}
        onInteraction={onFloatingWindowInteraction}
      >
        {content}
      </NarrativeOcgMobileFullScreen>
    )
  }

  return (
    <div
      className="narrative-ocg-dialog-backdrop"
      onClick={handleClose}
      onMouseDown={stopFloatingEvent}
      onTouchStart={stopFloatingEvent}
      onWheelCapture={event =>
        handleFloatingWheel(event, onFloatingWindowInteraction)
      }
      role="presentation"
    >
      <div
        className={`narrative-ocg-details-dialog ${maximized ? 'narrative-ocg-details-dialog--maximized' : ''}`}
        onClick={event => event.stopPropagation()}
        onMouseDown={event => event.stopPropagation()}
        onTouchStart={event => event.stopPropagation()}
        onWheelCapture={event =>
          handleFloatingWheel(event, onFloatingWindowInteraction)
        }
        role="dialog"
        aria-modal="true"
      >
        <div
          className={cn(
            'flex h-[80px] items-center justify-between px-6 text-white',
            severity === 'error' ? 'bg-[#B3261E]' : 'bg-[#FFA007]'
          )}
        >
          <h2 className="size-[18px] w-full font-bold leading-[30px] text-white">
            {getIssueTitle(severity, count)}
          </h2>
          <div className="inline-flex items-center">
            <BaseButton
              className="narrative-ocg-details-dialog__icon-button !min-h-0 !rounded !p-3 shadow-none"
              fillMode="flat"
              onClick={event => {
                onFloatingWindowInteraction?.()
                stopFloatingEvent(event)
                setMaximized(current => !current)
              }}
              themeColor="base"
              type="button"
              aria-label={maximized ? 'Restore details' : 'Maximize details'}
            >
              <img src={WINDOW_ICON} alt="" />
            </BaseButton>
            <BaseButton
              className="narrative-ocg-details-dialog__icon-button !min-h-0 !rounded !bg-transparent !p-3 shadow-none"
              fillMode="flat"
              onClick={handleClose}
              onMouseDown={handleClose}
              onTouchStart={handleClose}
              themeColor="base"
              type="button"
              aria-label="Close details"
            >
              <img src={CLOSE_DEFAULT} alt="" />
            </BaseButton>
          </div>
        </div>
        <div className="narrative-ocg-details-dialog__content">{content}</div>
      </div>
    </div>
  )
}
